import { parseTimeStamp, parseTimeStampOrNull } from '@/lib/parseTimeStamp';
import {
  DocumentObjectRow,
  ObjectListFrame,
  PatientObjectRow,
  PendingObjectRow,
  RoutineObjectRow,
  SessionObjectRow,
  TemplateObjectRow,
} from '@heidi/agents';
import {
  formatLastSeen,
  formatSessionWhen,
  objectDayLabel,
  patientDemographicsLabel,
  resolvePatientAvatarSeed,
  untitledTitle,
  type WrittenObject,
  type WrittenObjectKind,
} from '@heidi/agents/data';
import { calculateAge, useIdentityEditorIntl } from '@heidi/patients';
import type { FC } from 'react';
import { defineMessages, useIntl, type IntlShape } from 'react-intl';

import {
  useDocumentReference,
  usePatientReference,
  useRoutineReference,
  useSessionReference,
  useTemplateReference,
} from './chat-object-reference-hooks';
import { REFERENCE_KINDS, useOpenReference } from './chat-object-reference-kinds';
import { sessionGist, sessionStartedAt } from './chat-object-reference-label';
import { TaskRow } from './chat-object-task-row';

const messages = defineMessages({
  untitledRoutine: { defaultMessage: 'Untitled routine', id: 'ymuHIDgr1b' },
});

type RowProps = { object: WrittenObject };

const fallbackTitle = (object: WrittenObject, intl: IntlShape): string =>
  object.fallbackTitle ??
  (object.kind === 'routine'
    ? intl.formatMessage(messages.untitledRoutine)
    : untitledTitle(object.kind, intl));

const PatientRow = ({ object }: RowProps) => {
  const intl = useIntl();
  const identity = useIdentityEditorIntl();
  const openReference = useOpenReference();
  const ref = usePatientReference(object.id);
  const profile = ref.query.data;
  if (!ref.label || !profile)
    return <PendingObjectRow title={fallbackTitle(object, intl)} />;

  const destination = REFERENCE_KINDS.patient.open?.(
    { kind: 'patient', id: object.id },
    ref
  );
  const dob = profile.birth_date ? identity.formatDob(profile.birth_date) : null;
  const seen = parseTimeStampOrNull(profile.last_session_created_at);
  return (
    <PatientObjectRow
      name={ref.label}
      avatarSeed={resolvePatientAvatarSeed(profile)}
      demographics={patientDemographicsLabel(
        calculateAge(profile.birth_date),
        profile.gender,
        dob
      )}
      lastSeen={seen ? formatLastSeen(intl, seen) : undefined}
      onOpen={destination && (() => openReference(destination))}
    />
  );
};

const SessionRow = ({ object }: RowProps) => {
  const intl = useIntl();
  const openReference = useOpenReference();
  const ref = useSessionReference(object.id);
  const session = ref.query.data;
  if (!ref.label || !session)
    return <PendingObjectRow title={fallbackTitle(object, intl)} />;

  const destination = REFERENCE_KINDS.session.open?.(
    { kind: 'session', id: object.id },
    ref
  );
  const startedAt = sessionStartedAt(session);
  return (
    <SessionObjectRow
      title={ref.label}
      when={startedAt ? formatSessionWhen(intl, startedAt) : ref.label}
      gist={sessionGist(session, intl)}
      patientName={ref.patientName}
      avatarSeed={ref.avatarSeed}
      onOpen={destination && (() => openReference(destination))}
    />
  );
};

const DocumentRow = ({ object }: RowProps) => {
  const intl = useIntl();
  const openReference = useOpenReference();
  const sessionId = object.sessionId ?? '';
  const ref = useDocumentReference(object.id, sessionId);
  const sessionRef = useSessionReference(sessionId);
  const session = sessionRef.query.data;
  if (!ref.label || !sessionId) {
    return <PendingObjectRow title={fallbackTitle(object, intl)} />;
  }

  const startedAt = session ? sessionStartedAt(session) : null;
  const destination = REFERENCE_KINDS.document.open?.(
    { kind: 'document', id: object.id, sessionId },
    ref
  );
  return (
    <DocumentObjectRow
      title={ref.label}
      when={startedAt ? formatSessionWhen(intl, startedAt) : undefined}
      gist={session ? sessionGist(session, intl) : undefined}
      patientName={sessionRef.patientName}
      avatarSeed={sessionRef.avatarSeed}
      onOpen={destination && (() => openReference(destination))}
    />
  );
};

const TemplateRow = ({ object }: RowProps) => {
  const intl = useIntl();
  const openReference = useOpenReference();
  const ref = useTemplateReference(object.id);
  if (!ref.label) return <PendingObjectRow title={fallbackTitle(object, intl)} />;

  const destination = REFERENCE_KINDS.template.open?.(
    { kind: 'template', id: object.id },
    ref
  );
  return (
    <TemplateObjectRow
      title={ref.label}
      editedOn={objectDayLabel(ref.query.data?.updated_time, intl, parseTimeStamp)}
      onOpen={destination && (() => openReference(destination))}
    />
  );
};

const RoutineRow = ({ object }: RowProps) => {
  const intl = useIntl();
  const openReference = useOpenReference();
  const ref = useRoutineReference(object.id);
  if (!ref.label) return <PendingObjectRow title={fallbackTitle(object, intl)} />;

  const destination = REFERENCE_KINDS.routine.open?.(
    { kind: 'routine', id: object.id },
    ref
  );
  return (
    <RoutineObjectRow
      title={ref.label}
      description={ref.query.data?.description?.trim() || undefined}
      onOpen={destination && (() => openReference(destination))}
    />
  );
};

const ROWS: Record<WrittenObjectKind, FC<RowProps>> = {
  patient: PatientRow,
  session: SessionRow,
  task: TaskRow,
  document: DocumentRow,
  template: TemplateRow,
  routine: RoutineRow,
};

export const ChatObjectList = ({
  objects,
}: {
  objects: readonly WrittenObject[];
}): JSX.Element | null => {
  const [first] = objects;
  if (!first) return null;
  return (
    <ObjectListFrame
      kind={first.kind}
      items={objects}
      renderRow={(object) => {
        const Row = ROWS[object.kind];
        return <Row key={`${object.kind}:${object.id}`} object={object} />;
      }}
    />
  );
};
